import {
  AIAgentConversation,
  AIAgentDiagnostics,
  AIAgentIdentityStatus,
  AIAgentMappedError,
  AIAgentMessage,
  AIAgentNotice,
  AIAgentScreenContext,
  AIAgentSession,
} from './AIAgentTypes';
import { AI_AGENT_WELCOME_MESSAGE } from './AIAgentConstants';

export type AIAgentAction =
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'EXPAND' }
  | { type: 'RETURN_NORMAL' }
  | { type: 'SET_IDENTITY_STATUS'; identityStatus: AIAgentIdentityStatus }
  | { type: 'SET_SCREEN_CONTEXT'; screenContext: AIAgentScreenContext }
  | { type: 'SET_DRAFT'; draft: string }
  | { type: 'CLEAR_NOTICE' }
  | {
      type: 'SESSION_INITIALIZING';
    }
  | {
      type: 'SESSION_READY';
      conversationId: string;
    }
  | {
      type: 'SESSION_INVALID';
    }
  | {
      type: 'SEND_START';
      questionId: string;
      requestId: string;
      message: string;
      conversationEpoch: number;
    }
  | {
      type: 'SEND_SUCCESS';
      requestId: string;
      questionId: string;
      conversationEpoch: number;
      reply: string;
      diagnostics?: AIAgentDiagnostics;
    }
  | {
      type: 'SEND_ERROR';
      requestId: string;
      questionId: string;
      conversationEpoch: number;
      error: AIAgentMappedError;
    }
  | { type: 'STALE_COMPLETION' }
  | { type: 'RESET_START' }
  | { type: 'RESET_SUCCESS'; conversationId?: string }
  | { type: 'RESET_FAILURE'; message: string }
  | { type: 'RETRY'; requestId: string; questionId: string }
  | { type: 'DISCARD' };

let messageCounter = 0;

export function createMessageId(): string {
  messageCounter += 1;
  return `ai-msg-${messageCounter}-${Date.now()}`;
}

export function createRequestId(): string {
  return `ai-req-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createQuestionId(): string {
  return `ai-q-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function createWelcomeMessage(epoch: number): AIAgentMessage {
  return {
    id: createMessageId(),
    conversationEpoch: epoch,
    role: 'welcome',
    content: AI_AGENT_WELCOME_MESSAGE,
    state: 'settled',
    createdAt: new Date().toISOString(),
    action: 'none',
  };
}

function createInitialSession(epoch = 0): AIAgentSession {
  return {
    conversationEpoch: epoch,
    status: 'initializing',
  };
}

export function createInitialConversation(
  screenContext: AIAgentScreenContext,
  identityStatus: AIAgentIdentityStatus = 'resolving'
): AIAgentConversation {
  return {
    mode: 'closed',
    phase: 'idle',
    availability: 'active',
    identityStatus,
    conversationEpoch: 0,
    session: createInitialSession(0),
    messages: [createWelcomeMessage(0)],
    draft: '',
    screenContext,
  };
}

function isOperationBlocked(state: AIAgentConversation): boolean {
  return (
    state.identityStatus === 'resolving' ||
    state.identityStatus === 'invalid' ||
    state.phase === 'resetting'
  );
}

function replaceMessage(
  messages: AIAgentMessage[],
  messageId: string,
  next: AIAgentMessage
): AIAgentMessage[] {
  return messages.map((message) => (message.id === messageId ? next : message));
}

function findThinkingSlot(
  messages: AIAgentMessage[],
  questionId: string
): AIAgentMessage | undefined {
  return messages.find(
    (message) =>
      message.questionId === questionId &&
      (message.role === 'status' || message.role === 'error') &&
      (message.state === 'thinking' ||
        message.state === 'recoverable-error' ||
        message.state === 'terminal-error')
  );
}

export function aiAgentReducer(
  state: AIAgentConversation,
  action: AIAgentAction
): AIAgentConversation {
  switch (action.type) {
    case 'OPEN':
      return { ...state, mode: state.mode === 'expanded' ? 'expanded' : 'normal' };

    case 'CLOSE':
      return { ...state, mode: 'closed' };

    case 'EXPAND':
      return state.mode === 'closed' ? state : { ...state, mode: 'expanded' };

    case 'RETURN_NORMAL':
      return state.mode === 'closed' ? state : { ...state, mode: 'normal' };

    case 'SET_IDENTITY_STATUS':
      return { ...state, identityStatus: action.identityStatus };

    case 'SET_SCREEN_CONTEXT':
      return { ...state, screenContext: action.screenContext };

    case 'SET_DRAFT':
      return { ...state, draft: action.draft };

    case 'CLEAR_NOTICE':
      return { ...state, notice: undefined };

    case 'SESSION_INITIALIZING':
      return {
        ...state,
        session: {
          ...state.session,
          status: 'initializing',
          conversationId: undefined,
        },
      };

    case 'SESSION_READY':
      return {
        ...state,
        session: {
          conversationId: action.conversationId,
          conversationEpoch: state.conversationEpoch,
          status: 'active',
          createdAt: new Date().toISOString(),
        },
      };

    case 'SESSION_INVALID':
      return {
        ...state,
        session: {
          ...state.session,
          conversationId: undefined,
          status: 'invalid',
        },
      };

    case 'SEND_START': {
      if (
        isOperationBlocked(state) ||
        state.phase === 'sending' ||
        state.availability === 'conversation-limit'
      ) {
        return state;
      }

      const normalized = action.message.trim();
      if (!normalized) {
        return state;
      }

      const userMessage: AIAgentMessage = {
        id: createMessageId(),
        conversationEpoch: action.conversationEpoch,
        role: 'user',
        content: normalized,
        state: 'settled',
        createdAt: new Date().toISOString(),
        questionId: action.questionId,
        requestId: action.requestId,
        action: 'none',
      };

      const thinkingSlot: AIAgentMessage = {
        id: createMessageId(),
        conversationEpoch: action.conversationEpoch,
        role: 'status',
        content: '',
        state: 'thinking',
        createdAt: new Date().toISOString(),
        questionId: action.questionId,
        requestId: action.requestId,
        action: 'none',
      };

      return {
        ...state,
        phase: 'sending',
        draft: '',
        activeRequestId: action.requestId,
        activeQuestionId: action.questionId,
        notice: undefined,
        messages: [...state.messages, userMessage, thinkingSlot],
      };
    }

    case 'SEND_SUCCESS': {
      if (
        state.activeRequestId !== action.requestId ||
        state.activeQuestionId !== action.questionId ||
        state.conversationEpoch !== action.conversationEpoch
      ) {
        return state;
      }

      const slot = findThinkingSlot(state.messages, action.questionId);
      if (!slot) {
        return {
          ...state,
          phase: 'idle',
          activeRequestId: undefined,
          activeQuestionId: undefined,
        };
      }

      const assistantMessage: AIAgentMessage = {
        ...slot,
        role: 'assistant',
        content: action.reply,
        state: 'settled',
        requestId: action.requestId,
        diagnostics: action.diagnostics,
        action: 'none',
      };

      return {
        ...state,
        phase: 'idle',
        activeRequestId: undefined,
        activeQuestionId: undefined,
        messages: replaceMessage(state.messages, slot.id, assistantMessage),
      };
    }

    case 'SEND_ERROR': {
      if (
        state.activeRequestId !== action.requestId ||
        state.activeQuestionId !== action.questionId ||
        state.conversationEpoch !== action.conversationEpoch
      ) {
        return state;
      }

      const nextSession = action.error.isSessionInvalid
        ? {
            ...state.session,
            conversationId: undefined,
            status: 'invalid' as const,
          }
        : state.session;

      const slot = findThinkingSlot(state.messages, action.questionId);
      if (!slot) {
        return {
          ...state,
          phase: 'idle',
          activeRequestId: undefined,
          activeQuestionId: undefined,
          session: nextSession,
          availability: action.error.isConversationLimit
            ? 'conversation-limit'
            : state.availability,
        };
      }

      const errorMessage: AIAgentMessage = {
        ...slot,
        role: 'error',
        content: action.error.message,
        state: action.error.retryable ? 'recoverable-error' : 'terminal-error',
        requestId: action.requestId,
        action: action.error.action,
      };

      return {
        ...state,
        phase: 'idle',
        activeRequestId: undefined,
        activeQuestionId: undefined,
        session: nextSession,
        availability: action.error.isConversationLimit
          ? 'conversation-limit'
          : state.availability,
        messages: replaceMessage(state.messages, slot.id, errorMessage),
      };
    }

    case 'STALE_COMPLETION':
      return state;

    case 'RESET_START': {
      if (isOperationBlocked(state) || state.phase === 'resetting') {
        return state;
      }

      const messages = state.messages.map((message) => {
        if (message.state === 'thinking') {
          return {
            ...message,
            role: 'error' as const,
            content: 'Operação cancelada.',
            state: 'terminal-error' as const,
            action: 'none' as const,
          };
        }
        return message;
      });

      return {
        ...state,
        phase: 'resetting',
        activeRequestId: undefined,
        activeQuestionId: undefined,
        session: {
          ...state.session,
          status: 'replaced',
          conversationId: undefined,
        },
        messages,
        notice: undefined,
      };
    }

    case 'RESET_SUCCESS': {
      const nextEpoch = state.conversationEpoch + 1;
      return {
        ...state,
        phase: 'idle',
        availability: 'active',
        conversationEpoch: nextEpoch,
        session: {
          conversationId: action.conversationId,
          conversationEpoch: nextEpoch,
          status: action.conversationId ? 'active' : 'initializing',
          createdAt: action.conversationId
            ? new Date().toISOString()
            : undefined,
        },
        messages: [createWelcomeMessage(nextEpoch)],
        draft: '',
        activeRequestId: undefined,
        activeQuestionId: undefined,
        notice: undefined,
      };
    }

    case 'RESET_FAILURE': {
      const notice: AIAgentNotice = {
        kind: 'reset-failed',
        message: action.message,
        action: 'none',
      };

      return {
        ...state,
        phase: 'idle',
        notice,
      };
    }

    case 'RETRY': {
      if (
        isOperationBlocked(state) ||
        state.phase === 'sending' ||
        state.availability === 'conversation-limit'
      ) {
        return state;
      }

      const slot = state.messages.find(
        (message) =>
          message.questionId === action.questionId &&
          message.state === 'recoverable-error'
      );

      if (!slot || !slot.questionId) {
        return state;
      }

      const thinkingSlot: AIAgentMessage = {
        ...slot,
        role: 'status',
        content: '',
        state: 'thinking',
        requestId: action.requestId,
        action: 'none',
      };

      return {
        ...state,
        phase: 'sending',
        activeRequestId: action.requestId,
        activeQuestionId: action.questionId,
        messages: replaceMessage(state.messages, slot.id, thinkingSlot),
      };
    }

    case 'DISCARD':
      return createInitialConversation(state.screenContext, 'resolving');

    default:
      return state;
  }
}

export function canSendMessage(state: AIAgentConversation): boolean {
  return (
    state.identityStatus === 'valid' &&
    state.phase === 'idle' &&
    state.availability === 'active' &&
    state.mode !== 'closed'
  );
}

export function canResetConversation(state: AIAgentConversation): boolean {
  return state.identityStatus === 'valid' && state.phase !== 'resetting';
}

export function isInitialState(state: AIAgentConversation): boolean {
  return (
    state.messages.length === 1 &&
    state.messages[0].role === 'welcome' &&
    state.phase === 'idle'
  );
}

export function getOriginalQuestionContent(
  state: AIAgentConversation,
  questionId: string
): string | undefined {
  return state.messages.find(
    (message) => message.role === 'user' && message.questionId === questionId
  )?.content;
}
